/**
 * Transcription Service (VPS-Only Version)
 * Handles real-time speech-to-text via Web Speech API
 * Communicates with Node.js backend via Socket.IO
 * Stores transcripts as JSON files on VPS
 * 
 * Features:
 * - Auto-start on room join
 * - Real-time captions
 * - Transcript save to VPS (JSON files)
 * - Download as TXT/VTT/JSON
 * - Retrieve past transcripts (30-day retention)
 * - Auto-delete after 30 days
 */

class TranscriptionService {
  constructor() {
    this.recognition = null;
    this.isListening = false;
    this.transcript = ''; // Full transcript text
    this.captions = []; // Array of caption objects {timestamp, text, speaker}
    this.currentCaption = ''; // Current interim caption
    this.listeners = {}; // For event callbacks
    this.startTime = null;
    this.roomId = null;
    this.userId = null;
    this.userName = null;
    this.socket = null;
    this.recognitionSupported = this.checkSupportedBrowser();

    // Initialize Web Speech API
    if (this.recognitionSupported) {
      this.initializeRecognition();
    }
  }

  /**
   * Check if Web Speech API is supported
   */
  checkSupportedBrowser() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.warn('🎤 Web Speech API not supported in this browser');
      return false;
    }
    return true;
  }

  /**
   * Set Socket.IO instance for communication with backend
   */
  setSocket(socket) {
    this.socket = socket;
    console.log('🎤 Socket.IO instance set for transcription');
  }

  /**
   * Initialize Web Speech API recognition
   */
  initializeRecognition() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    this.recognition = new SpeechRecognition();

    // Configuration
    this.recognition.continuous = true; // Keep listening
    this.recognition.interimResults = true; // Show interim captions
    this.recognition.language = 'en-US'; // Default to English

    /**
     * Handle successful recognition result
     */
    this.recognition.onresult = (event) => {
      let interim = '';
      let final = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;

        if (event.results[i].isFinal) {
          final += transcript + ' ';
        } else {
          interim += transcript;
        }
      }

      // Update current interim caption (shows in real-time)
      this.currentCaption = interim;
      this.emit('caption-update', {
        text: interim,
        isFinal: false
      });

      // Add final caption to transcript
      if (final) {
        const timestamp = this.getTimestamp();
        const captionObject = {
          timestamp,
          text: final.trim(),
          speaker: this.userName || 'Unknown',
          userId: this.userId,
          isFinal: true
        };

        this.captions.push(captionObject);
        this.transcript += `[${timestamp}] ${this.userName}: ${final}\n`;
        this.currentCaption = ''; // Clear interim

        // Emit final caption event
        this.emit('caption-update', {
          text: final.trim(),
          isFinal: true,
          speaker: this.userName,
          timestamp
        });

        console.log(`🎤 Transcript: [${timestamp}] ${this.userName}: ${final.trim()}`);
      }
    };

    /**
     * Handle errors
     */
    this.recognition.onerror = (event) => {
      console.error(`🎤 Speech recognition error: ${event.error}`);
      this.emit('error', {
        error: event.error,
        message: this.getErrorMessage(event.error)
      });

      // Permission refused: stop trying, or the restart loop would run forever.
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        this.wantListening = false;
      }

      // Auto-restart on network error
      if (event.error === 'network') {
        console.log('🎤 Network error, restarting recognition...');
        setTimeout(() => this.startListening(), 2000);
      }
    };

    /**
     * Handle end of recognition
     */
    this.recognition.onend = () => {
      console.log('🎤 Recognition ended');
      this.isListening = false;
      this.emit('listening-stopped');

      // Chrome (especially on phones) ends continuous recognition by itself after silence or when the mic is shared.
      // Restart it while captions are still wanted. Do not reset the transcript here.
      if (this.wantListening) {
        setTimeout(() => {
          if (!this.wantListening || this.isListening) return;
          try {
            this.recognition.start();
            this.isListening = true;
            console.log('🎤 Restarted recognition');
          } catch (e) {
            console.warn('🎤 Could not restart recognition', e);
          }
        }, 400);
      }
    };

    console.log('🎤 Transcription service initialized');
  }

  /**
   * Start listening (auto-called on room join)
   */
  startListening() {
    if (!this.recognitionSupported) {
      console.warn('🎤 Web Speech API not supported');
      return false;
    }

    if (this.isListening) {
      console.log('🎤 Already listening');
      return false;
    }

    try {
      this.recognition.start();
      this.isListening = true;
      this.wantListening = true;
      this.startTime = new Date();
      this.transcript = '';
      this.captions = [];
      this.currentCaption = '';

      console.log('🎤 Transcription started');
      this.emit('listening-started');
      return true;
    } catch (error) {
      console.error('🎤 Error starting recognition:', error);
      return false;
    }
  }

  /**
   * Stop listening
   */
  stopListening() {
    if (!this.isListening) {
      console.log('🎤 Not currently listening');
      return false;
    }

    try {
      this.wantListening = false;
      this.recognition.stop();
      this.isListening = false;
      console.log('🎤 Transcription stopped');
      this.emit('listening-stopped');
      return true;
    } catch (error) {
      console.error('🎤 Error stopping recognition:', error);
      return false;
    }
  }

  /**
   * Set user and room context
   */
  setContext(roomId, userId, userName) {
    this.roomId = roomId;
    this.userId = userId;
    this.userName = userName;
    console.log(`🎤 Context set: Room=${roomId}, User=${userName}`);
  }

  /**
   * Change language
   */
  setLanguage(languageCode) {
    if (this.recognition) {
      this.recognition.language = languageCode;
      console.log(`🎤 Language changed to: ${languageCode}`);
    }
  }

  /**
   * Get current transcript text
   */
  getTranscript() {
    return this.transcript;
  }

  /**
   * Get captions array
   */
  getCaptions() {
    return this.captions;
  }

  /**
   * Get formatted timestamp (HH:MM:SS)
   */
  getTimestamp() {
    if (!this.startTime) return '00:00:00';

    const elapsed = Date.now() - this.startTime.getTime();
    const seconds = Math.floor(elapsed / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    return `${String(hours).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  }

  /**
   * Get error message from error code
   */
  getErrorMessage(errorCode) {
    const errors = {
      'no-speech': 'No speech detected. Please try again.',
      'audio-capture': 'No microphone found. Check permissions.',
      'network': 'Network error. Reconnecting...',
      'aborted': 'Recognition was aborted.',
      'service-not-allowed': 'Speech Recognition service not allowed.'
    };
    return errors[errorCode] || 'Unknown error occurred.';
  }

  /**
   * Save transcript to VPS via Socket.IO
   */
  async saveToDatabase(meetingData = {}) {
    if (!this.transcript || this.captions.length === 0) {
      console.warn('🎤 No transcript to save');
      return false;
    }

    if (!this.socket) {
      console.error('🎤 Socket.IO not connected');
      return false;
    }

    try {
      const payload = {
        roomId: this.roomId,
        userId: this.userId,
        userName: this.userName,
        transcript: this.transcript,
        captions: this.captions,
        duration: this.getTimestamp(),
        language: this.recognition?.language || 'en-US',
        ...meetingData
      };

      console.log('🎤 Saving transcript to VPS...', payload);

      // Send via Socket.IO to Node.js backend
      this.socket.emit('save-transcript', payload);

      // Wait for response
      return new Promise((resolve) => {
        this.socket.once('transcript-saved', (response) => {
          console.log('✅ Transcript saved on VPS:', response);
          this.emit('saved', response);
          resolve(response);
        });

        this.socket.once('transcript-save-error', (error) => {
          console.error('❌ Error saving transcript:', error);
          this.emit('save-error', error);
          resolve(false);
        });

        // Timeout after 10 seconds
        setTimeout(() => {
          console.warn('🎤 Transcript save timeout');
          resolve(false);
        }, 10000);
      });

    } catch (error) {
      console.error('❌ Error saving transcript:', error);
      this.emit('save-error', error);
      return false;
    }
  }

  /**
   * Get past transcripts for room from VPS
   */
  async getRoomTranscripts() {
    if (!this.socket || !this.roomId) {
      console.error('🎤 Socket.IO not connected or roomId not set');
      return [];
    }

    try {
      console.log('🎤 Fetching room transcripts from VPS...');

      this.socket.emit('get-room-transcripts', { roomId: this.roomId });

      return new Promise((resolve) => {
        this.socket.once('room-transcripts', (response) => {
          console.log('📝 Room transcripts retrieved:', response.transcripts);
          resolve(response.transcripts || []);
        });

        this.socket.once('transcripts-error', (error) => {
          console.error('❌ Error fetching transcripts:', error);
          resolve([]);
        });

        setTimeout(() => {
          console.warn('🎤 Fetch transcripts timeout');
          resolve([]);
        }, 5000);
      });

    } catch (error) {
      console.error('❌ Error in getRoomTranscripts:', error);
      return [];
    }
  }

  /**
   * Get specific transcript from VPS
   */
  async fetchSavedTranscript(fileName) {
    if (!this.socket || !this.roomId) {
      console.error('🎤 Socket.IO not connected or roomId not set');
      return null;
    }

    try {
      console.log('🎤 Fetching transcript from VPS:', fileName);

      this.socket.emit('get-transcript', { roomId: this.roomId, fileName });

      return new Promise((resolve) => {
        this.socket.once('transcript-data', (transcript) => {
          console.log('📝 Transcript data retrieved');
          resolve(transcript);
        });

        this.socket.once('transcript-error', (error) => {
          console.error('❌ Error fetching transcript:', error);
          resolve(null);
        });

        setTimeout(() => {
          console.warn('🎤 Fetch transcript timeout');
          resolve(null);
        }, 5000);
      });

    } catch (error) {
      console.error('❌ Error in getTranscript:', error);
      return null;
    }
  }

  /**
   * Delete transcript from VPS
   */
  async deleteTranscript(fileName) {
    if (!this.socket || !this.roomId) {
      console.error('🎤 Socket.IO not connected or roomId not set');
      return false;
    }

    try {
      console.log('🎤 Deleting transcript from VPS:', fileName);

      this.socket.emit('delete-transcript', { roomId: this.roomId, fileName });

      return new Promise((resolve) => {
        this.socket.once('transcript-deleted', (response) => {
          console.log('✅ Transcript deleted');
          resolve(true);
        });

        this.socket.once('transcript-error', (error) => {
          console.error('❌ Error deleting transcript:', error);
          resolve(false);
        });

        setTimeout(() => {
          console.warn('🎤 Delete transcript timeout');
          resolve(false);
        }, 5000);
      });

    } catch (error) {
      console.error('❌ Error in deleteTranscript:', error);
      return false;
    }
  }

  /**
   * Download transcript as TXT
   */
  downloadAsTxt() {
    const filename = `transcript-${this.roomId}-${new Date().toISOString().slice(0, 10)}.txt`;
    const content = `Meeting Transcript\nRoom: ${this.roomId}\nRecorded: ${this.startTime || new Date()}\n\n${this.transcript}`;
    this.downloadFile(filename, content, 'text/plain');
  }

  /**
   * Download transcript as VTT (for video subtitles)
   */
  downloadAsVtt() {
    const filename = `transcript-${this.roomId}-${new Date().toISOString().slice(0, 10)}.vtt`;
    let vttContent = 'WEBVTT\n\n';

    this.captions.forEach((caption) => {
      const startTime = this.formatVttTime(caption.timestamp);
      const endTime = this.formatVttTime(caption.timestamp);
      vttContent += `${startTime} --> ${endTime}\n${caption.speaker}: ${caption.text}\n\n`;
    });

    this.downloadFile(filename, vttContent, 'text/vtt');
  }

  /**
   * Download transcript as JSON (full metadata)
   */
  downloadAsJson() {
    const filename = `transcript-${this.roomId}-${new Date().toISOString().slice(0, 10)}.json`;
    const jsonData = {
      roomId: this.roomId,
      userId: this.userId,
      userName: this.userName,
      startTime: this.startTime,
      endTime: new Date(),
      duration: this.getTimestamp(),
      language: this.recognition?.language || 'en-US',
      captions: this.captions,
      transcript: this.transcript
    };

    this.downloadFile(filename, JSON.stringify(jsonData, null, 2), 'application/json');
  }

  /**
   * Format timestamp for VTT format (HH:MM:SS.mmm)
   */
  formatVttTime(timestamp) {
    return `${timestamp}.000`;
  }

  /**
   * Trigger file download
   */
  downloadFile(filename, content, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log(`📥 Downloaded: ${filename}`);
  }

  /**
   * Event listener registration
   */
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  /**
   * Remove event listener
   */
  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }

  /**
   * Emit event
   */
  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(data));
    }
  }

  /**
   * Clear all data
   */
  clear() {
    this.transcript = '';
    this.captions = [];
    this.currentCaption = '';
    this.startTime = null;
    console.log('🎤 Transcription cleared');
  }
}

// Export singleton instance
const transcriptionService = new TranscriptionService();
export default transcriptionService;
